import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'react-toastify';
import axiosInstance from '../lib/axios';
import {
  Box, Typography, CircularProgress, Breadcrumbs,
  Link, Tooltip, IconButton, Button, TextField,
  Grid, Stack, Paper
} from '@mui/material';
import { usePermissions } from '../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const UpcomingCheckinList = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().add(1, 'month').format('YYYY-MM-DD'));
  const UserId = sessionStorage?.getItem('UserId');
  const permissions = usePermissions("Check-Out");
  const { AddRec = "0" } = permissions || {};
  const gridRef = useRef();
  const navigate = useNavigate();
  const globalProperty = useSelector(selectGlobalProperty);

  const colDefs = useMemo(() => [
    { field: 'RowNum', headerName: 'Sr.No', width: 60 },
    { field: 'BookNo', headerName: 'Booking No', flex: 1 },
    { field: 'BookDt', headerName: 'Book Date', flex: 1, filter: 'agDateColumnFilter' },
    { field: 'ResidentName', headerName: 'Resident', flex: 1 },
    { field: 'MobileNo', headerName: 'Mobile', flex: 1 },
    { field: 'Duration', headerName: 'Duration', flex: 1 },
    { field: 'PropName', headerName: 'Property', flex: 1 },
    { field: 'BookDtFrom', headerName: 'Checkin', flex: 1, filter: 'agDateColumnFilter' },
    { field: 'BookUpToDt', headerName: 'Checkout', flex: 1, filter: 'agDateColumnFilter' },
    { field: 'Remark', headerName: 'Remark', flex: 1 },
    {
      field: 'BookStatus', headerName: 'BookSt', flex: 1,
      cellStyle: (params) => ({
        color: params.value === "CheckOut" ? 'red' : 'green',
        fontWeight: 'bold',
      })
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    width: 100,
    tooltipValueGetter: (params) => params.value ?? '',
  }), []);

  useEffect(() => {
    fetchCheckout();
  }, [globalProperty]);

  const fetchCheckout = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(`TrnBooking/GetFilterTrnBookingDashBoard`,
        {
          PageNumber: 0,
          PageSize: 0,
          SearchText: "",
          PropId: globalProperty,
          Flag: "UpCheckin",
          SearchFrom: fromDate,
          SearchTo: toDate
        }
      );

      if (response.data.Status === 0) {
        const formattedData = response.data.Data.map(bconfirm => ({
          ...bconfirm,
          BookDt: bconfirm.BookDt ? moment(bconfirm.BookDt).format('DD-MM-YYYY') : '',
          BookDtFrom: bconfirm.BookDtFrom ? moment(bconfirm.BookDtFrom).format('DD-MM-YYYY') : '',
          BookUpToDt: bconfirm.BookUpToDt ? moment(bconfirm.BookUpToDt).format('DD-MM-YYYY') : '',
          BookSt: bconfirm.BookSt === "C" ? "Confirmed" : bconfirm.BookSt === "B" ? "Booked" : ""
        }));
        setRowData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching check-out data:', error);
      toast.error('Error fetching check-out data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetData = () => {
    // Validate date range
    if (moment(fromDate).isAfter(toDate)) {
      toast.error('From date cannot be after To date');
      return;
    }

    fetchCheckout();
  };

  const handleClearDates = () => {
    setFromDate(moment().format('YYYY-MM-DD'));
    setToDate(moment().add(1, 'month').format('YYYY-MM-DD'));
  };

  const onGridReady = useCallback((params) => {
    // No sizeColumnsToFit here to preserve flex sizing
  }, []);

  // const handleRowDoubleClick = useCallback((params) => {
  //   const bookId = params.data.BookId;
  //   navigate('/checkout', { state: { BookId: bookId, mode: 'view' } });
  // }, [navigate]);

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      navigate('/checkout');
    }
  };

  const handleClearFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '90vh',
        padding: '10px',
        backgroundColor: 'rgb(249, 249, 249)',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "12px", sm: "16px 20px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: { xs: "5px auto", sm: "10px auto" },
          maxWidth: "95%",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{
              fontSize: { xs: "12px", sm: "15px" },
            }}
          >
            <Link
              underline="hover"
              color="#635bff"
              sx={{
                cursor: "pointer",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                fontSize: 'inherit'
              }}
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <Box component="span">Home</Box>
            </Link>

            <Link
              underline="hover"
              color="#635bff"
              sx={{
                cursor: "pointer",
                fontWeight: "500",
                fontSize: 'inherit'
              }}
              onClick={() => navigate('/dashboard')}
            >
              dashboard
            </Link>

            <Typography color="text.primary" sx={{ fontWeight: "500", fontSize: 'inherit' }}>
              Upcoming CheckinList
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: { xs: '100%', sm: '150px' },
              minWidth: '150px'
            }}
          />

          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: { xs: '100%', sm: '150px' },
              minWidth: '150px'
            }}
          />

          <Button
            variant="contained"
            onClick={handleGetData}
            sx={{
              backgroundColor: '#635bff',
              borderRadius: '50px',
              textTransform: 'none',
              px: 2,
              minWidth: '100px',
              '&:hover': {
                backgroundColor: '#524ccc',
              },
            }}
          >
            Get Data
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title='Clear all applied filter' arrow>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  borderColor: "#635bff",
                  color: "#635bff",
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: 'auto',
                  px: { xs: 2, sm: 3 },
                  "&:hover": {
                    borderColor: "#524ccc",
                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                  }
                }}
              >
                {'Clear Filters'}
              </Button>
            </Tooltip>

            <Tooltip title='Previous Page' arrow>
              <IconButton
                onClick={() => navigate('/dashboard')}
                sx={{
                  backgroundColor: '#635bff',
                  borderRadius: '50%',
                  width: { xs: 32, sm: 35 },
                  height: { xs: 32, sm: 35 },
                  '&:hover': {
                    backgroundColor: '#635bff',
                  },
                  boxShadow: 3,
                }}
              >
                <ArrowBackIcon sx={{
                  color: 'white',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
      <Box className="table-grid ag-theme-alpine " sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}>
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
              Loading data...
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={1000}
            // onRowDoubleClicked={handleRowDoubleClick}
            paginationPageSizeSelector={[100, 500, 1000, 5000]}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
            rowHeight={25}
            headerHeight={30}
            floatingFiltersHeight={28}
            overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
            tooltipShowDelay={0}
          />
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 70,
          fontSize: '14px',
          color: 'black',
        }}
      >
        Total Records: <b>{rowData.length}</b>
      </Box>
    </Box>
  );
};

export default UpcomingCheckinList;